import { ThemeEvents } from '@theme/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const STOREFRONT_API_VERSION = '2026-07';
const MAX_BUNDLE_COMPONENTS = 30;

const BUNDLE_COMPONENTS_QUERY = `
  query BundleComponents($id: ID!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    node(id: $id) {
      ... on ProductVariant {
        requiresComponents
        components(first: ${MAX_BUNDLE_COMPONENTS}) {
          nodes {
            quantity
            productVariant {
              id
              title
              price {
                amount
                currencyCode
              }
              product {
                title
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * @typedef {Object} BundleComponent
 * @property {number} quantity
 * @property {{
 *   id: string,
 *   title: string,
 *   price: { amount: string, currencyCode: string },
 *   product: { title: string }
 * }} productVariant
 */

/**
 * Displays the live component price breakdown for a fixed Shopify bundle.
 * Non-bundle products remain hidden.
 */
class BundleBreakdown extends HTMLElement {
  /** @type {Map<string, BundleComponent[] | null>} */
  static cache = new Map();

  /** @type {AbortController | undefined} */
  #lifecycleController;

  /** @type {AbortController | undefined} */
  #requestController;

  /** @type {BundleComponent[]} */
  #components = [];

  #quantity = 1;

  connectedCallback() {
    this.#lifecycleController = new AbortController();
    const { signal } = this.#lifecycleController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#quantity = this.#getCurrentQuantity();
    this.#loadVariant(this.dataset.variantId);
  }

  disconnectedCallback() {
    this.#lifecycleController?.abort();
    this.#requestController?.abort();
  }

  /** @param {CustomEvent} event */
  #handleVariantUpdate = (event) => {
    const { data, resource } = event.detail ?? {};

    if (data?.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (String(data?.productId) !== this.dataset.productId) {
      return;
    }

    this.dataset.variantId = resource?.id ? String(resource.id) : '';
    this.#quantity = this.#getCurrentQuantity();
    this.#loadVariant(this.dataset.variantId);
  };

  /** @param {CustomEvent} event */
  #handleQuantityUpdate = (event) => {
    const productForm = this.closest('product-form-component');
    const eventTarget = event.target instanceof Element ? event.target.closest('product-form-component') : null;

    if (!productForm || eventTarget !== productForm) return;

    this.#quantity = Math.max(1, Number(event.detail?.quantity) || 1);
    this.#renderPrices();
  };

  #getCurrentQuantity() {
    const input = this.closest('product-form-component')?.querySelector('input[name="quantity"]');
    return Math.max(1, Number(input?.value) || 1);
  }

  /** @param {string | undefined} variantId */
  async #loadVariant(variantId) {
    this.#requestController?.abort();

    if (!variantId) {
      this.#hide();
      return;
    }

    const gid = variantId.startsWith('gid://') ? variantId : `gid://shopify/ProductVariant/${variantId}`;
    this.#setLoading(true);

    try {
      const cachedComponents = BundleBreakdown.cache.get(gid);
      const components = cachedComponents === undefined ? await this.#fetchComponents(gid) : cachedComponents;

      if (cachedComponents === undefined) BundleBreakdown.cache.set(gid, components);
      if (!components?.length) {
        this.#hide();
        return;
      }

      this.#components = components;
      this.#renderRows();
      this.hidden = false;
      this.#setError(false);
      this.#setLoading(false);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.error('Unable to load Shopify bundle components.', error);
      if (this.#components.length) {
        this.#setError(true);
        this.hidden = false;
      } else {
        this.#hide();
      }
    }
  }

  /** @param {string} gid */
  async #fetchComponents(gid) {
    this.#requestController = new AbortController();
    const { signal } = this.#requestController;

    const response = await fetch(`https://${this.dataset.storeDomain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: BUNDLE_COMPONENTS_QUERY,
        variables: {
          id: gid,
          country: this.dataset.country || null,
          language: this.dataset.language || null,
        },
      }),
      signal,
    });

    if (!response.ok) throw new Error(`Storefront API returned ${response.status}.`);

    const payload = await response.json();
    if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join(' '));

    const variant = payload.data?.node;
    if (!variant?.requiresComponents) return null;

    return variant.components?.nodes ?? null;
  }

  #renderRows() {
    const rows = this.querySelector('[data-bundle-rows]');
    if (!(rows instanceof HTMLTableSectionElement)) return;

    rows.replaceChildren();
    const fragment = document.createDocumentFragment();

    for (const component of this.#components) {
      const row = document.createElement('tr');
      row.className = 'bundle-breakdown__row';

      const name = document.createElement('th');
      name.className = 'bundle-breakdown__name';
      name.scope = 'row';

      const productTitle = document.createElement('span');
      productTitle.className = 'bundle-breakdown__product-title';
      productTitle.textContent = component.productVariant.product.title;
      name.append(productTitle);

      if (component.productVariant.title !== 'Default Title') {
        const variantTitle = document.createElement('span');
        variantTitle.className = 'bundle-breakdown__variant-title';
        variantTitle.textContent = component.productVariant.title;
        name.append(variantTitle);
      }

      name.title = [component.productVariant.product.title, component.productVariant.title]
        .filter((title) => title !== 'Default Title')
        .join(' — ');

      const unitPrice = document.createElement('td');
      unitPrice.className = 'bundle-breakdown__number bundle-breakdown__unit-price';
      unitPrice.dataset.unitPrice = '';

      const quantity = document.createElement('td');
      quantity.className = 'bundle-breakdown__number bundle-breakdown__quantity';
      quantity.dataset.componentQuantity = String(component.quantity);

      const lineTotal = document.createElement('td');
      lineTotal.className = 'bundle-breakdown__number bundle-breakdown__line-total';
      lineTotal.dataset.lineTotal = '';

      row.append(name, unitPrice, quantity, lineTotal);
      fragment.append(row);
    }

    rows.append(fragment);
    this.#renderPrices();
  }

  #renderPrices() {
    if (!this.#components.length) return;

    const rows = this.querySelectorAll('.bundle-breakdown__row');
    let grandTotal = 0;
    let currency = '';

    rows.forEach((row, index) => {
      const component = this.#components[index];
      if (!component) return;

      const { amount, currencyCode } = component.productVariant.price;
      const unitPrice = convertMoneyToMinorUnits(amount, currencyCode);
      if (unitPrice === null) return;

      currency ||= currencyCode;
      const componentQuantity = component.quantity * this.#quantity;
      const lineTotal = unitPrice * componentQuantity;
      grandTotal += lineTotal;

      this.#setAnimatedText(row.querySelector('[data-unit-price]'), this.#formatPrice(unitPrice, currencyCode));
      this.#setAnimatedText(row.querySelector('[data-component-quantity]'), String(componentQuantity));
      this.#setAnimatedText(row.querySelector('[data-line-total]'), this.#formatPrice(lineTotal, currencyCode));
    });

    const bundleWord = this.#quantity === 1 ? this.dataset.bundleSingular : this.dataset.bundlePlural;
    const replacements = { __COUNT__: String(this.#quantity), __BUNDLE__: bundleWord ?? '' };

    this.#setTextFromTemplate('[data-bundle-count]', this.dataset.countTemplate, replacements);
    this.#setTextFromTemplate('[data-bundle-total-label]', this.dataset.totalTemplate, replacements);
    this.#setAnimatedText(
      this.querySelector('[data-bundle-grand-total]'),
      this.#formatPrice(grandTotal, currency || this.dataset.currency || '')
    );
  }

  /** @param {string} selector @param {string | undefined} template @param {Record<string, string>} replacements */
  #setTextFromTemplate(selector, template, replacements) {
    const element = this.querySelector(selector);
    if (!element || !template) return;

    element.textContent = Object.entries(replacements).reduce(
      (value, [token, replacement]) => value.replaceAll(token, replacement),
      template
    );
  }

  /** @param {number} value @param {string} currency */
  #formatPrice(value, currency) {
    return formatMoney(value, this.dataset.moneyFormat || '{{amount}}', currency);
  }

  /** @param {Element | null} element @param {string} value */
  #setAnimatedText(element, value) {
    if (!element || element.textContent === value) return;

    element.textContent = value;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    element.classList.remove('bundle-breakdown__value--updated');
    requestAnimationFrame(() => element.classList.add('bundle-breakdown__value--updated'));
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    this.toggleAttribute('aria-busy', loading);
    this.classList.toggle('bundle-breakdown--loading', loading);
  }

  /** @param {boolean} hasError */
  #setError(hasError) {
    this.classList.toggle('bundle-breakdown--error', hasError);
    this.querySelector('[data-bundle-content]')?.toggleAttribute('hidden', hasError);
    this.querySelector('[data-bundle-error]')?.toggleAttribute('hidden', !hasError);
    this.#setLoading(false);
  }

  #hide() {
    this.#components = [];
    this.hidden = true;
    this.#setLoading(false);
    this.#setError(false);
  }
}

if (!customElements.get('bundle-breakdown')) {
  customElements.define('bundle-breakdown', BundleBreakdown);
}
